import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import type { BranchDto } from "libs/cellera-sdk";

export interface PracticeUser {
  id: string;
  accountId: string;
  email: string;
  name: string;
  phoneNumber: string;
  role: string;
  npi?: string;
  branches: BranchDto[];
  specialties: string[];
  status: "active" | "inactive";
  addedDate: string;
}

interface PracticeUserTableProps {
  practiceUsers: PracticeUser[];
  loading: boolean;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: string, sortOrder: "asc" | "desc") => void;
  onUserClick: (userId: string) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

const PracticeUserTable: React.FC<PracticeUserTableProps> = ({
  practiceUsers,
  loading,
  sortBy,
  sortOrder,
  onSortChange,
  onUserClick,
  pagination,
}) => {
  const columns = useMemo(
    () => [
      { id: "accountId", label: "Account ID", sortable: true },
      { id: "email", label: "Email", sortable: true },
      { id: "name", label: "Name", sortable: true },
      { id: "phoneNumber", label: "Phone Number", sortable: true },
      { id: "role", label: "Role", sortable: false },
      { id: "npi", label: "NPI", sortable: true },
      { id: "branches", label: "Branch", sortable: false },
      { id: "specialties", label: "Specialty", sortable: false },
      { id: "status", label: "Status", sortable: false },
      { id: "addedDate", label: "Added Date", sortable: true },
      { id: "actions", label: "", sortable: false },
    ],
    [],
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  };

  const renderBranches = (branches: BranchDto[]) => {
    if (!branches.length) return "-";
    const maxDisplay = 3;
    const visibleBranches = branches.slice(0, maxDisplay);
    const remainingCount = branches.length - maxDisplay;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visibleBranches.map((branch) => (
          <Chip
            key={branch.id ?? branch.address}
            label={branch.address}
            size="small"
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#374151",
              "& .MuiChip-label": { fontSize: "0.75rem", px: 1 },
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  All Branches:
                </Typography>
                {branches.map((branch) => (
                  <Box
                    key={
                      branch.id ?? `${branch.address}-${branch.emailAddress}`
                    }
                  >
                    <Typography variant="body2">• {branch.address}</Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {branch.emailAddress} • {branch.primaryPhoneNumber}
                    </Typography>
                  </Box>
                ))}
              </Box>
            }
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  "& .MuiTooltip-arrow": {
                    color: "#ffffff",
                    "&::before": {
                      border: "1px solid #E5E7EB",
                    },
                  },
                },
              },
            }}
          >
            <Chip
              label={`(+${remainingCount} more)`}
              size="small"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  px: 1,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderSpecialties = (specialties: string[]) => {
    if (!specialties.length) return "-";
    const maxDisplay = 5;
    const visibleSpecialties = specialties.slice(0, maxDisplay);
    const remainingCount = specialties.length - maxDisplay;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visibleSpecialties.map((specialty) => (
          <Chip
            key={specialty}
            label={specialty}
            size="small"
            sx={{
              backgroundColor: "#DBEAFE",
              color: "#1E3A8A",
              "& .MuiChip-label": { fontSize: "0.75rem", px: 1 },
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  All Specialties:
                </Typography>
                {specialties.map((specialty) => (
                  <Typography key={specialty} variant="body2">
                    • {specialty}
                  </Typography>
                ))}
              </Box>
            }
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  "& .MuiTooltip-arrow": {
                    color: "#ffffff",
                    "&::before": {
                      border: "1px solid #E5E7EB",
                    },
                  },
                },
              },
            }}
          >
            <Chip
              label={`(+${remainingCount} more)`}
              size="small"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  px: 1,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const getStatusChip = (status: "active" | "inactive") => (
    <Chip
      label={status === "active" ? "Active" : "Inactive"}
      size="small"
      sx={{
        backgroundColor: status === "active" ? "#D1FAE5" : "#FEE2E2",
        color: status === "active" ? "#065F46" : "#991B1B",
        "& .MuiChip-label": { fontSize: "0.75rem", fontWeight: 500 },
      }}
    />
  );

  const handleSort = (column: string) => {
    const nextOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(column, nextOrder);
  };

  if (loading && practiceUsers.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2,
        overflow: "visible",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <TableContainer
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    borderBottom: "1px solid #E5E7EB",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortOrder : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {practiceUsers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  No practice users found
                </TableCell>
              </TableRow>
            )}

            {practiceUsers.map((user) => (
              <TableRow
                key={user.id}
                hover
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F3F4F6" },
                }}
                onClick={() => onUserClick(user.id)}
              >
                <TableCell
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                  }}
                >
                  {user.accountId}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{user.name}</TableCell>
                <TableCell>{user.phoneNumber}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role.replace("_", " ").toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: "#F3F4F6",
                      color: "#374151",
                      "& .MuiChip-label": {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {user.npi || "-"}
                </TableCell>
                <TableCell>{renderBranches(user.branches)}</TableCell>
                <TableCell>{renderSpecialties(user.specialties)}</TableCell>
                <TableCell>{getStatusChip(user.status)}</TableCell>
                <TableCell sx={{ color: "#4B5563" }}>
                  {formatDate(user.addedDate)}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <RowActions onViewDetails={() => onUserClick(user.id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1}
        onPageChange={(_, newPage) => pagination.onPageChange(newPage + 1)}
        rowsPerPage={pagination.pageSize}
        onRowsPerPageChange={(event) =>
          pagination.onPageSizeChange(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={[10, 20, 50]}
      />
    </Paper>
  );
};

type RowActionsProps = {
  onViewDetails: () => void;
};

const RowActions: React.FC<RowActionsProps> = ({ onViewDetails }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleView = () => {
    handleClose();
    onViewDetails();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleView}>View details</MenuItem>
      </Menu>
    </>
  );
};

export default PracticeUserTable;
